import React, { useEffect, useState } from "react";
import { Zap, ScanLine, Mic, Trophy, Award } from "lucide-react";

const GOLD = "#D4AF37", SRC = "/marketing/ci-ad-source.png", W = 940, H = 1672;
const PILLARS = [
  { Icon: Zap, label: "Technical Engine", sub: ["Every rep", "graded"] },
  { Icon: ScanLine, label: "Skeletal Tracking", sub: ["13-point", "joint scan"] },
  { Icon: Mic, label: "Voice Feedback", sub: ["Coach in", "your ear"] },
  { Icon: Trophy, label: "Local Leaderboard", sub: ["Central Illinois", "rankings"] },
  { Icon: Award, label: "Official Enrollment", sub: ["Volume 1", "certificate"] },
];

// Soft-edged patch: a blurred, darkened copy of the source underneath, so replaced regions blend into the artwork.
const Patch = ({ x, y, w, h, blur = 16, dim = 0.5, feather = "62%" }) => (
  <div className="absolute overflow-hidden" style={{ left: x, top: y, width: w, height: h, WebkitMaskImage: `radial-gradient(ellipse at center, #000 ${feather}, transparent 100%)`, maskImage: `radial-gradient(ellipse at center, #000 ${feather}, transparent 100%)` }}>
    <div className="absolute" style={{ inset: -60, backgroundImage: `url(${SRC})`, backgroundSize: `${W}px ${H}px`, backgroundPosition: `${-(x - 60)}px ${-(y - 60)}px`, filter: `blur(${blur}px) brightness(${dim}) saturate(0.85)` }} />
  </div>
);

// Cleanup of the user's AI ad: official stacked crest, correct brand spelling, app-native footer iconography.
export default function PrintCIAd() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    Promise.all([document.fonts.ready, ...[SRC, "/brand/nexto-academy-logo.png"].map((s) => new Promise((r) => { const i = new Image(); i.onload = i.onerror = r; i.src = s; }))]).then(() => setReady(true));
  }, []);
  return (
    <div className="bg-black min-h-screen">
      <div data-testid="print-ci-ad" data-ready={ready ? "1" : "0"} className="relative overflow-hidden text-white" style={{ width: W, height: H, fontFamily: "Montserrat, sans-serif" }}>
        <img src={SRC} alt="" className="absolute inset-0" style={{ width: W, height: H }} />

        {/* 1. Logo swap — mask the generic crest, drop in the official stacked Academy crest */}
        <Patch x={285} y={10} w={380} h={370} blur={22} dim={0.42} feather="55%" />
        <div className="absolute flex items-center justify-center" style={{ left: 285, top: 22, width: 380, height: 340 }}>
          <img src="/brand/nexto-academy-logo.png" alt="The Next O Technical Academy" style={{ height: 312, filter: `drop-shadow(0 0 22px ${GOLD}77) drop-shadow(0 6px 18px rgba(0,0,0,.8))` }} />
        </div>

        {/* 4. Metrics panel — replace Speed / Distance / Heart Rate with Hub-tracked technical metrics */}
        <Patch x={478} y={362} w={160} h={272} blur={30} dim={0.36} feather="76%" />
        <div className="absolute flex flex-col gap-[14px]" style={{ left: 494, top: 380, width: 128 }}>
          {[["Balance Score", "9.2", "/10", 92], ["Execution", "9.4", "/10", 94], ["Rep Count", "12,480", "reps", 78]].map(([k, v, u, pct]) => (
            <div key={k} className="border-l-2 pl-2.5" style={{ borderColor: GOLD }}>
              <div className="font-mono uppercase text-white/85 whitespace-nowrap" style={{ fontSize: 11, letterSpacing: "0.14em" }}>{k}</div>
              <div className="flex items-baseline gap-1 leading-none mt-0.5"><span className="font-cover" style={{ fontSize: 30 }}>{v}</span><span className="font-mono text-white/70" style={{ fontSize: 11 }}>{u}</span></div>
              <div className="mt-1.5 h-[3px] w-full rounded-full" style={{ background: "rgba(255,255,255,.14)" }}><div className="h-full rounded-full" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${GOLD}, #F0D77A)`, boxShadow: `0 0 6px ${GOLD}` }} /></div>
            </div>
          ))}
        </div>

        {/* 2. Footer icons — five app-native pillars */}
        <Patch x={0} y={1336} w={940} h={214} blur={44} dim={0.34} feather="82%" />
        <div className="absolute flex justify-between" style={{ left: 30, top: 1366, width: 880 }}>
          {PILLARS.map(({ Icon, label, sub }, i) => (
            <div key={label} className="relative flex flex-col items-center text-center" style={{ width: 172 }}>
              {i > 0 && <span className="absolute left-[-2px] top-2 h-[150px] w-px" style={{ background: `linear-gradient(180deg, transparent, ${GOLD}88, transparent)` }} />}
              <div className="rounded-full flex items-center justify-center border-2" style={{ width: 66, height: 66, borderColor: GOLD, background: `radial-gradient(circle at 35% 30%, ${GOLD}33, rgba(8,8,8,.92) 70%)`, boxShadow: `0 0 22px ${GOLD}66, inset 0 0 10px ${GOLD}22` }}>
                <Icon size={30} color={GOLD} strokeWidth={2.2} />
              </div>
              <div className="font-headings uppercase mt-3 leading-none whitespace-nowrap" style={{ fontSize: 18, letterSpacing: "0.02em", textShadow: "0 2px 6px rgba(0,0,0,.9)" }}>{label}</div>
              <div className="font-mono uppercase text-white/70 leading-tight mt-1.5" style={{ fontSize: 11, letterSpacing: "0.12em" }}>{sub[0]}<br />{sub[1]}</div>
            </div>
          ))}
        </div>

        {/* 3. Footer wordmark + strapline with correct spelling */}
        <Patch x={0} y={1540} w={940} h={92} blur={40} dim={0.34} feather="82%" />
        <div className="absolute left-0 right-0 flex flex-col items-center" style={{ top: 1552 }}>
          <div className="flex items-center gap-4">
            <span className="h-px w-16" style={{ background: `${GOLD}aa` }} />
            <div className="font-headings uppercase" style={{ fontSize: 30, letterSpacing: "0.32em", color: GOLD, textShadow: `0 0 14px ${GOLD}55` }}>The Next O Technical Academy</div>
            <span className="h-px w-16" style={{ background: `${GOLD}aa` }} />
          </div>
          <div className="font-mono font-bold uppercase text-white/85 mt-2" style={{ fontSize: 13, letterSpacing: "0.3em" }}>
            Technical Training <span style={{ color: "#C41E24" }}>|</span> Biomechanics <span style={{ color: "#C41E24" }}>|</span> Performance <span style={{ color: "#C41E24" }}>|</span> Opportunity
          </div>
        </div>
      </div>
    </div>
  );
}
